import {
  Award_Per_Counting_Strategy,
  Earning_Mechanism_Product_Counting_Attribute,
  Offer_Engagement_Type,
} from '@occc/graphql';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { getEarningMechanismAward } from '@occc-app/offerHelpers/utils/getEarningMechanismAward';

import { OfferSchema, OfferSchemaValidate, OfferSubtype, OfferType } from './types';
import { supportedOfferValues } from './constants';

const validateVoucherAwardVoucherCount: OfferSchemaValidate = (offer) => {
  const VOUCHER_COUNT = 1;

  if (!offer.earningMechanism) {
    return true;
  }

  const award = getEarningMechanismAward(offer.earningMechanism);

  if (award && awardHelper.isVouchersAward(award)) {
    return award.voucherCount === VOUCHER_COUNT;
  }

  return true;
};

export const rewardSchema: OfferSchema = {
  type: OfferType.Reward,
  engagementType: Offer_Engagement_Type.Purchaseable,
  earningMechanism: ['EarningMechanismPointSpend'],
  awards: ['AwardVouchers'],
  // TODO: it would be handy to combine limits that are used together
  // For example, this list of limits is used in 10 places.
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintInAudience',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
  validate: validateVoucherAwardVoucherCount,
};

export const promotionProductDiscountSchema: OfferSchema = {
  type: OfferType.Promotion,
  subtype: OfferSubtype.ProductDiscount,
  engagementType: Offer_Engagement_Type.Promotional,
  earningMechanism: ['EarningMechanismProductDiscount'],
  awards: ['AwardPriceOverride', 'AwardMoneyDiscount'],
  limits: [
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitInTransactionAwardCount',
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintRequiresActivation',
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintPurchaseThreshold',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
  validate: (offer) => {
    if (!offer.earningMechanism) {
      return true;
    }

    const award = getEarningMechanismAward(offer.earningMechanism);

    if (award && awardHelper.isMoneyDiscountAward(award)) {
      return (
        award.awardPer === Award_Per_Counting_Strategy.IndividualPurchase ||
        award.awardPer === Award_Per_Counting_Strategy.SellingUnitPurchase
      );
    }

    return true;
  },
};

export const promotionTransactionDiscountSchema: OfferSchema = {
  type: OfferType.Promotion,
  subtype: OfferSubtype.TransactionDiscount,
  engagementType: Offer_Engagement_Type.Promotional,
  earningMechanism: ['EarningMechanismTransactionDiscount'],
  awards: ['AwardTransactionPercentDiscount', 'AwardTransactionMoneyDiscount'],
  limits: [
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitInTransactionAwardCount',
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintRequiresActivation',
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintPurchaseThreshold',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
};

export const promotionVarietyDiscountSchema: OfferSchema = {
  type: OfferType.Promotion,
  subtype: OfferSubtype.VarietyDiscount,
  engagementType: Offer_Engagement_Type.Promotional,
  earningMechanism: ['EarningMechanismVarietyDiscount'],
  awards: ['AwardTransactionMoneyDiscount', 'AwardProductDiscount', 'AwardPriceOverride'],
  limits: [
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitInTransactionAwardCount',
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintRequiresActivation',
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintPurchaseThreshold',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
};

export const promotionBogoSchema: OfferSchema = {
  type: OfferType.Promotion,
  subtype: OfferSubtype.BOGO,
  engagementType: Offer_Engagement_Type.Promotional,
  earningMechanism: ['EarningMechanismProductDiscount'],
  awards: ['AwardBuyAndGet'],
  limits: [
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitInTransactionAwardCount',
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintRequiresActivation',
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintPurchaseThreshold',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
  validate: (offer) => {
    const PERCENT_DISCOUNT = 100;

    if (!offer.earningMechanism) {
      return true;
    }

    const award = getEarningMechanismAward(offer.earningMechanism);

    if (award && awardHelper.isBuyAndGetAward(award)) {
      return award.percentDiscount === PERCENT_DISCOUNT;
    }

    return true;
  },
};

export const clubOrPunchcardSchema: OfferSchema = {
  type: OfferType.ClubOrPunchcard,
  engagementType: Offer_Engagement_Type.PunchcardChallenge,
  earningMechanism: ['EarningMechanismVarietyPurchaseOverTime'],
  awards: ['AwardPoints', 'AwardVouchers'],
  limits: [
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitMembershipProgressInTime',
    'LimitInTransactionProgress',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
  validate: (offer) => {
    const PRODUCT_ITEMS_COUNT = 1;
    const COUNT_PRODUCTS_BY = Earning_Mechanism_Product_Counting_Attribute.Quantity;

    if (!offer.earningMechanism) {
      return true;
    }

    if (earningMechanismHelper.isVarietyPurchaseOverTime(offer.earningMechanism)) {
      return (
        offer.earningMechanism.products.length === PRODUCT_ITEMS_COUNT &&
        offer.earningMechanism.products[0].countProductsBy === COUNT_PRODUCTS_BY &&
        offer.earningMechanism.preventDoublePunches === true
      );
    }

    return true;
  },
};

export const offerProductPurchaseSchema: OfferSchema = {
  type: OfferType.Offer,
  subtype: OfferSubtype.ProductPurchase,
  engagementType: Offer_Engagement_Type.Transactional,
  earningMechanism: ['EarningMechanismProductPurchase'],
  awards: ['AwardPoints', 'AwardVouchers', 'AwardExternal'],
  limits: [
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitInTransactionAwardCount',
    'LimitGlobalAwardCount', // Requires to support Bybe offer (editing Bybe offers is disabled)
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintPurchaseThreshold',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintEmailRequired', // Requires to support Bybe offer (editing Bybe offers is disabled)
    'ConstraintInStoreOnly',
    'ConstraintOrderAheadOnly',
  ],
  validate: validateVoucherAwardVoucherCount,
};

export const offerSpendBonusSchema: OfferSchema = {
  type: OfferType.Offer,
  subtype: OfferSubtype.SpendBonus,
  engagementType: Offer_Engagement_Type.Transactional,
  earningMechanism: ['EarningMechanismTotalSpendOverTime'],
  awards: ['AwardPoints', 'AwardVouchers'],
  limits: [
    ...supportedOfferValues.limits,
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintInStoreOnly',
    'ConstraintOrderAheadOnly',
  ],
  validate: validateVoucherAwardVoucherCount,
};

export const offerBaseEarnSchema: OfferSchema = {
  type: OfferType.Offer,
  subtype: OfferSubtype.BaseEarn,
  engagementType: Offer_Engagement_Type.Transactional,
  earningMechanism: ['EarningMechanismTotalSpendOnTransaction'],
  awards: ['AwardPointsPer'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintPurchaseThreshold',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintInStoreOnly',
    'ConstraintOrderAheadOnly',
  ],
};

export const offerBaseEligibleEarnSchema: OfferSchema = {
  type: OfferType.Offer,
  subtype: OfferSubtype.BaseEligibleEarn,
  engagementType: Offer_Engagement_Type.Transactional,
  earningMechanism: ['EarningMechanismTotalEligiblePurchase'],
  awards: ['AwardPointsPer'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintRequiresActivation',
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintPurchaseThreshold',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintInStoreOnly',
    'ConstraintOrderAheadOnly',
  ],
};

export const offerExternalFulfillmentSchema: OfferSchema = {
  type: OfferType.Offer,
  subtype: OfferSubtype.ExternalFulfillment,
  engagementType: Offer_Engagement_Type.ExternallyFulfillable,
  earningMechanism: ['EarningMechanismExternalFulfillment'],
  awards: ['AwardPoints', 'AwardVouchers', null],
  limits: [
    ...supportedOfferValues.limits,
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintEmailRequired',
    'ConstraintSubBrand',
    'ConstraintInStoreOnly',
    'ConstraintOrderAheadOnly',
  ],
};

export const challengeVisitChallengeSchema: OfferSchema = {
  type: OfferType.Challenge,
  subtype: OfferSubtype.VisitChallenge,
  engagementType: Offer_Engagement_Type.Transactional,
  earningMechanism: ['EarningMechanismVisits'],
  awards: ['AwardPoints', 'AwardVouchers'],
  limits: [
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitMembershipProgressInTime',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
  validate: validateVoucherAwardVoucherCount,
};

export const challengeVarietyChallengeSchema: OfferSchema = {
  type: OfferType.Challenge,
  subtype: OfferSubtype.VarietyChallenge,
  engagementType: Offer_Engagement_Type.Transactional,
  earningMechanism: ['EarningMechanismVarietyPurchase', 'EarningMechanismVarietyPurchaseOverTime'],
  awards: ['AwardPoints', 'AwardVouchers'],
  limits: [
    'LimitGlobalActivation',
    'LimitGlobalActivationInTime',
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipActivation',
    'LimitMembershipActivationInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitInTransactionProgress',
    'LimitInTransactionAwardCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPurchaseThreshold',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
  validate: (offer) => {
    const isValidVoucherCount = validateVoucherAwardVoucherCount(offer);
    if (!isValidVoucherCount) {
      return false;
    }

    if (!offer.earningMechanism) {
      return true;
    }

    if (earningMechanismHelper.isVarietyPurchaseOverTime(offer.earningMechanism)) {
      return offer.earningMechanism.preventDoublePunches === false;
    }

    return true;
  },
};

export const sweepstakesOfferProductPurchaseSchema: OfferSchema = {
  type: OfferType.Sweepstakes,
  subtype: OfferSubtype.ProductPurchase,
  engagementType: Offer_Engagement_Type.SweepstakesTransactional,
  earningMechanism: ['EarningMechanismProductPurchase'],
  awards: ['AwardVouchers'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
  ],
  validate: validateVoucherAwardVoucherCount,
};

export const sweepstakesOfferVarietyChallengeSchema: OfferSchema = {
  type: OfferType.Sweepstakes,
  subtype: OfferSubtype.VarietyChallenge,
  engagementType: Offer_Engagement_Type.SweepstakesTransactional,
  earningMechanism: ['EarningMechanismVarietyPurchase', 'EarningMechanismVarietyPurchaseOverTime'],
  awards: ['AwardVouchers'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPurchaseThreshold',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
  ],
  validate: (offer) => {
    const isValidVoucherCount = validateVoucherAwardVoucherCount(offer);
    if (!isValidVoucherCount) {
      return false;
    }

    if (!offer.earningMechanism) {
      return true;
    }

    if (earningMechanismHelper.isVarietyPurchaseOverTime(offer.earningMechanism)) {
      return offer.earningMechanism.preventDoublePunches === false;
    }

    return true;
  },
};

export const sweepstakesOfferSpendBonusSchema: OfferSchema = {
  type: OfferType.Sweepstakes,
  subtype: OfferSubtype.SpendBonus,
  engagementType: Offer_Engagement_Type.SweepstakesTransactional,
  earningMechanism: ['EarningMechanismTotalSpendOverTime'],
  awards: ['AwardVouchers'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintRequiresActivation',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
  ],
  validate: validateVoucherAwardVoucherCount,
};

export const sweepstakesRewardSchema: OfferSchema = {
  type: OfferType.Sweepstakes,
  engagementType: Offer_Engagement_Type.SweepstakesPurchasable,
  earningMechanism: ['EarningMechanismPointSpend'],
  awards: ['AwardVouchers'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintLocationInState',
    'ConstraintInAudience',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
  ],
  validate: validateVoucherAwardVoucherCount,
};

export const awardTransactionDiscountSchema: OfferSchema = {
  type: OfferType.Award,
  subtype: OfferSubtype.TransactionDiscount,
  engagementType: Offer_Engagement_Type.Vouchered,
  earningMechanism: ['EarningMechanismTransactionDiscount'],
  awards: ['AwardTransactionMoneyDiscount', 'AwardTransactionPercentDiscount'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintRequiresVoucherAvailableForDiscount',
    'ConstraintDaysOfWeek',
    'ConstraintTimeOfDay',
    'ConstraintLocationInState',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
};

export const awardProductDiscountSchema: OfferSchema = {
  type: OfferType.Award,
  subtype: OfferSubtype.ProductDiscount,
  engagementType: Offer_Engagement_Type.Vouchered,
  earningMechanism: ['EarningMechanismProductDiscount'],
  awards: ['AwardPriceOverride', 'AwardMoneyDiscount'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintRequiresVoucherAvailableForDiscount',
    'ConstraintDaysOfWeek',
    'ConstraintTimeOfDay',
    'ConstraintLocationInState',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
  validate: (offer) => {
    const PURCHASE_THRESHOLD_INT = 1;

    if (!offer.earningMechanism) {
      return true;
    }

    if (!earningMechanismHelper.isProductDiscount(offer.earningMechanism)) {
      return true;
    }

    const award = getEarningMechanismAward(offer.earningMechanism);
    const isSupportedAwardType =
      !!award &&
      (awardHelper.isPriceOverrideAward(award) || awardHelper.isMoneyDiscountAward(award));

    if (isSupportedAwardType) {
      return offer.earningMechanism.purchaseThresholdInt === PURCHASE_THRESHOLD_INT;
    }

    return true;
  },
};

export const awardFuelRollbackSchema: OfferSchema = {
  type: OfferType.Award,
  subtype: OfferSubtype.FuelRollback,
  engagementType: Offer_Engagement_Type.Vouchered,
  earningMechanism: ['EarningMechanismProductDiscount'],
  awards: ['AwardMoneyDiscount'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitInTransactionAwardCount',
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintRequiresTransactionVoucher',
    'ConstraintLocationInState',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintPreAuth',
    'ConstraintDaysOfWeek',
    'ConstraintTimeOfDay',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
    'ConstraintInStoreOnly',
  ],
  requiredValues: {
    constraints: ['ConstraintRequiresTransactionVoucher', 'ConstraintPreAuth'],
  },
  validate: (offer) => {
    const PURCHASE_THRESHOLD_INT = 1;
    const AWARD_PER = Award_Per_Counting_Strategy.IndividualPurchase;

    if (!offer.earningMechanism) {
      return true;
    }

    const award = getEarningMechanismAward(offer.earningMechanism);

    if (
      award &&
      awardHelper.isMoneyDiscountAward(award) &&
      earningMechanismHelper.isProductDiscount(offer.earningMechanism)
    ) {
      return (
        award.awardPer === AWARD_PER &&
        offer.earningMechanism.purchaseThresholdInt === PURCHASE_THRESHOLD_INT
      );
    }

    return true;
  },
};

export const awardSweepstakesEntrySchema: OfferSchema = {
  type: OfferType.Award,
  subtype: OfferSubtype.SweepstakeEntry,
  engagementType: Offer_Engagement_Type.SweepstakesEntry,
  earningMechanism: [null],
  awards: [],
  limits: [],
  constraints: ['ConstraintRequiresVoucher', 'ConstraintSubBrand', 'ConstraintOrderAheadOnly'],
  requiredValues: {
    constraints: ['ConstraintRequiresVoucher'],
  },
};

export const awardExternalFulfillmentSchema: OfferSchema = {
  type: OfferType.Award,
  subtype: OfferSubtype.ExternalFulfillment,
  engagementType: Offer_Engagement_Type.Vouchered,
  earningMechanism: ['EarningMechanismExternalFulfillment'],
  awards: ['AwardPoints', 'AwardVouchers', null],
  limits: [],
  constraints: ['ConstraintRequiresVoucher', 'ConstraintSubBrand', 'ConstraintOrderAheadOnly'],
  requiredValues: {
    constraints: ['ConstraintRequiresVoucher'],
  },
};

export const promotionFuelRollbackSchema: OfferSchema = {
  type: OfferType.Promotion,
  subtype: OfferSubtype.FuelRollback,
  engagementType: Offer_Engagement_Type.Promotional,
  earningMechanism: ['EarningMechanismProductDiscount'],
  awards: ['AwardMoneyDiscount'],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
    'LimitInTransactionAwardCount',
    'LimitMembershipTransactionCount',
    'LimitMembershipTransactionCountInTime',
  ],
  constraints: [
    'ConstraintPreAuth',
    'ConstraintInAudience',
    'ConstraintLocationInState',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintPaymentMethod',
    'ConstraintPaymentInstrument',
    'ConstraintSubBrand',
    'ConstraintInStoreOnly',
    'ConstraintOrderAheadOnly',
  ],
  requiredValues: {
    constraints: ['ConstraintPreAuth'],
  },
  validate: (offer) => {
    const PURCHASE_THRESHOLD_INT = 1;

    if (
      offer.earningMechanism &&
      earningMechanismHelper.isProductDiscount(offer.earningMechanism)
    ) {
      return offer.earningMechanism.purchaseThresholdInt === PURCHASE_THRESHOLD_INT;
    }

    return true;
  },
};

export const promocodesSchema: OfferSchema = {
  type: OfferType.Promocodes,
  engagementType: Offer_Engagement_Type.Promocode,
  earningMechanism: ['EarningMechanismPromocodes'],
  awards: ['AwardPoints', 'AwardVouchers', null],
  limits: [
    'LimitGlobalAwardCount',
    'LimitGlobalAwardCountInTime',
    'LimitMembershipAwardCount',
    'LimitMembershipAwardCountInTime',
  ],
  constraints: [
    'ConstraintTimeOfDay',
    'ConstraintDaysOfWeek',
    'ConstraintInAudience',
    'ConstraintPurchasableEligibility',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
  ],
};

export const billboardSchema: OfferSchema = {
  type: OfferType.Billboard,
  engagementType: Offer_Engagement_Type.Billboard,
  earningMechanism: [null],
  awards: [],
  limits: [],
  constraints: [
    'ConstraintInAudience',
    'ConstraintLocationInState',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
  ],
};

export const loyaltyEventSchema: OfferSchema = {
  type: OfferType.LoyaltyEvent,
  engagementType: Offer_Engagement_Type.Behavioral,
  earningMechanism: ['EarningMechanismLoyaltyEvent'],
  awards: ['AwardExternalPoints', 'AwardPoints', 'AwardVouchers', null],
  limits: [...supportedOfferValues.limits],
  constraints: [...supportedOfferValues.constraints, 'ConstraintOrderAheadOnly'],
};

export const storedValueSchema: OfferSchema = {
  type: OfferType.StoredValue,
  engagementType: Offer_Engagement_Type.StoredValue,
  earningMechanism: ['EarningMechanismStoredValueDiscount'],
  awards: [],
  limits: [...supportedOfferValues.limits],
  constraints: [...supportedOfferValues.constraints, 'ConstraintOrderAheadOnly'],
};

export const freeGameSchema: OfferSchema = {
  type: OfferType.FreeGame,
  engagementType: Offer_Engagement_Type.Game,
  earningMechanism: ['EarningMechanismSpinWheelGame'],
  awards: [],
  limits: [...supportedOfferValues.limits],
  constraints: [
    'ConstraintInAudience',
    'ConstraintLocationInState',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
  ],
};

export const voucheredGameSchema: OfferSchema = {
  type: OfferType.VoucheredGame,
  engagementType: Offer_Engagement_Type.GameVoucher,
  earningMechanism: ['EarningMechanismSpinWheelGame'],
  awards: [],
  limits: [...supportedOfferValues.limits],
  constraints: [
    'ConstraintRequiresVoucher',
    'ConstraintInAudience',
    'ConstraintLocationInState',
    'ConstraintStoreLocation',
    'ConstraintStoreLocationGroup',
    'ConstraintSubBrand',
    'ConstraintOrderAheadOnly',
  ],
};

export const schemaList = [
  rewardSchema,
  promotionProductDiscountSchema,
  promotionTransactionDiscountSchema,
  promotionVarietyDiscountSchema,
  promotionBogoSchema,
  promocodesSchema,
  promotionFuelRollbackSchema,
  clubOrPunchcardSchema,
  offerProductPurchaseSchema,
  offerSpendBonusSchema,
  offerBaseEarnSchema,
  offerBaseEligibleEarnSchema,
  offerExternalFulfillmentSchema,
  challengeVisitChallengeSchema,
  challengeVarietyChallengeSchema,
  awardProductDiscountSchema,
  awardTransactionDiscountSchema,
  awardSweepstakesEntrySchema,
  awardFuelRollbackSchema,
  awardExternalFulfillmentSchema,
  sweepstakesOfferProductPurchaseSchema,
  sweepstakesOfferVarietyChallengeSchema,
  sweepstakesOfferSpendBonusSchema,
  sweepstakesRewardSchema,
  billboardSchema,
  loyaltyEventSchema,
  storedValueSchema,
  freeGameSchema,
  voucheredGameSchema,
];
